import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@components/PageHeader';
import { Card } from '@components/ui/Card';
import { Select } from '@components/ui/Select';
import { Input } from '@components/ui/Input';
import { Autocomplete, AutocompleteOption } from '@components/ui/Autocomplete';
import { Button } from '@components/ui/Button';
import { Tabs } from '@components/ui/Tabs';
import { EmptyState } from '@components/ui/EmptyState';
import { AUSTRALIAN_STATES } from '@utils/constants';
import {
  searchParcelsNearPoint,
  ParcelResult,
  getGoogleAddressSuggestions,
  getGooglePlaceDetails,
  GoogleAddressSuggestion,
  findLotAtPoint,
  parseNswLotPlan,
} from '@services';
import { useToast } from '@hooks';
import { useEstateStore } from '../../stores/estateStore';
import { AustralianState, AddressLookupResult } from '@types';
import type { EstateJsonData } from '@types';
import { jsonDataToItems, jsonDataToMetadata } from '../../utils/mergeEstates';

type SearchMode = 'planNumber' | 'address';

export const FindEstate = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [selectedState, setSelectedState] = useState<AustralianState>('VIC');
  const [searchMode, setSearchMode] = useState<SearchMode>('planNumber');
  const [planNumber, setPlanNumber] = useState('');
  const [address, setAddress] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<GoogleAddressSuggestion[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [selectedAddressSuggestion, setSelectedAddressSuggestion] =
    useState<GoogleAddressSuggestion | null>(null);
  const [addressLookup, setAddressLookup] = useState<AddressLookupResult | null>(null);
  const [parcelResults, setParcelResults] = useState<ParcelResult[]>([]);
  const [searchRadius, setSearchRadius] = useState<number>(500); // Default 500m radius
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nswLotResult, setNswLotResult] = useState<{
    lotNumber: string;
    planLabel: string;
    area: number;
    address: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { loadEstateWithItems } = useEstateStore();

  const handleImportJson = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string) as EstateJsonData;

          if (!data.lots || !Array.isArray(data.lots)) {
            toast('Invalid JSON: missing "lots" array', 'error');
            return;
          }

          const planLabel = file.name.replace(/\.json$/i, '');
          const importedMetadata = jsonDataToMetadata(data);

          loadEstateWithItems(
            {
              ...importedMetadata,
              planNumber: planLabel,
              name: data.name || planLabel,
            },
            jsonDataToItems(data)
          );

          navigate(`/edit/${encodeURIComponent(planLabel)}`);
        } catch {
          toast('Failed to parse JSON file', 'error');
        }
      };
      reader.readAsText(file);

      // Reset input so the same file can be re-selected
      event.target.value = '';
    },
    [loadEstateWithItems, navigate, toast]
  );

  // Debounced address search (VIC + NSW both use Google Places)
  useEffect(() => {
    if ((selectedState !== 'VIC' && selectedState !== 'NSW') || searchMode !== 'address') {
      return;
    }

    if (address.length < 2) {
      setAddressSuggestions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsLoadingSuggestions(true);
      setError(null); // Clear any previous errors
      try {
        const suggestions = await getGoogleAddressSuggestions(address);
        console.log('Received suggestions:', suggestions.length);
        setAddressSuggestions(suggestions);
      } catch (error) {
        console.error('Failed to fetch address suggestions:', error);
        setAddressSuggestions([]); // Clear suggestions on error
        setError('Failed to fetch address suggestions');
      } finally {
        setIsLoadingSuggestions(false);
      }
    }, 300); // 300ms debounce for Google Places API

    return () => clearTimeout(timeoutId);
  }, [address, selectedState, searchMode]);

  const handleSearchByPlanNumber = () => {
    if (!planNumber.trim()) {
      toast('Please enter a plan number', 'warning');
      return;
    }

    if (selectedState === 'NSW') {
      const parsed = parseNswLotPlan(planNumber.trim());
      if (!parsed) {
        toast(
          'For NSW enter a lot/plan like "1/DP831464", "1 DP831464", or just "DP831464"',
          'warning'
        );
        return;
      }
      navigate(`/edit/${parsed.planLabel}`, {
        state: { lotNumber: parsed.lotNumber, state: 'NSW' },
      });
    } else {
      // VIC and others: navigate directly — ReviewEdit submits the backend job
      navigate(`/edit/${planNumber.trim().toUpperCase()}`, {
        state: { state: selectedState },
      });
    }
  };

  const handleSearchByAddress = async () => {
    if (selectedState === 'VIC') {
      if (!selectedAddressSuggestion) {
        toast('Please select an address from the suggestions', 'warning');
        return;
      }

      setIsSearching(true);
      setError(null);
      setAddressLookup(null);
      setParcelResults([]);

      try {
        const placeDetails = await getGooglePlaceDetails(selectedAddressSuggestion.placeId);

        if (!placeDetails) {
          throw new Error('Failed to get address details from Google Places');
        }

        const lookupResult = {
          address: placeDetails.address,
          coordinates: placeDetails.coordinates,
          lga: '',
          planning: { overlays: [] },
        };

        setAddressLookup(lookupResult);

        const parcels = await searchParcelsNearPoint(
          placeDetails.coordinates.lat,
          placeDetails.coordinates.lng,
          searchRadius
        );

        setParcelResults(parcels);

        if (parcels.length > 0) {
          toast(`Found ${parcels.length} parcel(s) within ${searchRadius}m`, 'success');
        } else {
          toast(`No parcels found within ${searchRadius}m — try increasing the radius`, 'warning');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to lookup address';
        setError(errorMessage);
        toast(errorMessage, 'error');
      } finally {
        setIsSearching(false);
      }
    } else if (selectedState === 'NSW') {
      if (!selectedAddressSuggestion) {
        toast('Please select an address from the suggestions', 'warning');
        return;
      }

      setIsSearching(true);
      setError(null);
      setAddressLookup(null);
      setNswLotResult(null);

      try {
        const placeDetails = await getGooglePlaceDetails(selectedAddressSuggestion.placeId);
        if (!placeDetails) {
          throw new Error('Failed to get address details from Google Places');
        }

        setAddressLookup({
          address: placeDetails.address,
          coordinates: placeDetails.coordinates,
          lga: '',
          planning: { overlays: [] },
        });

        // Find the cadastral lot at this address using NSW SIX API
        const lotAtPoint = await findLotAtPoint(
          placeDetails.coordinates.lat,
          placeDetails.coordinates.lng
        );

        if (lotAtPoint) {
          setNswLotResult({ ...lotAtPoint, address: placeDetails.address });
          toast(`Found Lot ${lotAtPoint.lotNumber} ${lotAtPoint.planLabel}`, 'success');
        } else {
          toast(
            'No NSW cadastral lot found at this address. Try the Lot/Plan tab instead.',
            'warning'
          );
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to lookup address';
        setError(errorMessage);
        toast(errorMessage, 'error');
      } finally {
        setIsSearching(false);
      }
    } else {
      toast(
        `Address search is not yet available for ${selectedState}. Please use Plan Number search instead.`,
        'warning'
      );
    }
  };

  const handleSearch = () => {
    if (searchMode === 'planNumber') {
      handleSearchByPlanNumber();
    } else {
      handleSearchByAddress();
    }
  };

  const handleAddressSelect = useCallback(
    (option: AutocompleteOption) => {
      // Find the matching suggestion
      const suggestion = addressSuggestions.find((s) => s.id === option.id);
      if (suggestion) {
        setSelectedAddressSuggestion(suggestion);
      }
    },
    [addressSuggestions]
  );

  const autocompleteOptions: AutocompleteOption[] = addressSuggestions.map((s) => ({
    id: s.placeId,
    label: s.description,
    value: s.description,
  }));

  return (
    <div>
      <PageHeader title="Find Estate" subtitle="Search for estates by plan number or address" />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card>
            <div className="mb-6">
              <Select
                label="State"
                options={AUSTRALIAN_STATES}
                value={selectedState}
                onChange={(e) => {
                  setSelectedState(e.target.value as AustralianState);
                  setAddressSuggestions([]);
                  setSelectedAddressSuggestion(null);
                  setNswLotResult(null);
                  setAddressLookup(null);
                  setParcelResults([]);
                }}
              />
              {selectedState === 'VIC' && searchMode === 'address' && (
                <div className="mt-3 rounded-lg bg-gold-light p-3">
                  <p className="text-xs font-bold text-gold">✓ Victoria: Using Google Places API</p>
                  <p className="mt-1 text-xs text-gray-600">
                    Fast, reliable address autocomplete powered by Google
                  </p>
                </div>
              )}
              {selectedState === 'NSW' && searchMode === 'address' && (
                <div className="mt-3 rounded-lg bg-blue-50 p-3">
                  <p className="text-xs font-bold text-blue-700">
                    ✓ NSW: Using Google Places + NSW SIX Cadastre
                  </p>
                  <p className="mt-1 text-xs text-gray-600">
                    Address resolves to a cadastral lot via the NSW Government SIX API
                  </p>
                </div>
              )}
            </div>

            {selectedState === 'VIC' || selectedState === 'NSW' ? (
              <Tabs
                tabs={[
                  {
                    label: 'Plan Number',
                    content: (
                      <div>
                        <Input
                          label={selectedState === 'NSW' ? 'Lot / Plan Number' : 'Plan Number'}
                          placeholder={
                            selectedState === 'NSW'
                              ? 'e.g., 1/DP831464  or  1 DP831464  or  DP831464'
                              : 'e.g., PS123456, DP654321'
                          }
                          value={planNumber}
                          onChange={(e) => {
                            setPlanNumber(e.target.value);
                            setSearchMode('planNumber');
                          }}
                          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        {selectedState === 'NSW' && (
                          <p className="mb-4 text-xs text-gray-500">
                            Enter the <strong>parent plan</strong> that was subdivided into the
                            estate. Use <strong>DP831464</strong> to search across all lots in that
                            plan, or <strong>1/DP831464</strong> to search within a specific lot
                            only. The subdivision plan is detected automatically.
                          </p>
                        )}
                        <Button onClick={handleSearch} isLoading={isSearching}>
                          {selectedState === 'NSW' ? 'Find Subdivision' : 'Search by Plan Number'}
                        </Button>
                      </div>
                    ),
                  },
                  {
                    label: 'Address',
                    content: (
                      <div>
                        <Autocomplete
                          label={selectedState === 'NSW' ? 'NSW Address' : 'Victorian Address'}
                          placeholder={
                            selectedState === 'NSW'
                              ? 'e.g., 167 Ingleburn Road, Leppington'
                              : 'e.g., 65 Main Street Winchelsea'
                          }
                          value={address}
                          onChange={(newValue) => {
                            setAddress(newValue);
                            setSearchMode('address');
                            if (
                              selectedAddressSuggestion &&
                              newValue !== selectedAddressSuggestion.description
                            ) {
                              setSelectedAddressSuggestion(null);
                              setError(null);
                              setAddressLookup(null);
                              setParcelResults([]);
                              setNswLotResult(null);
                            }
                          }}
                          onSelect={handleAddressSelect}
                          suggestions={autocompleteOptions}
                          isLoading={isLoadingSuggestions}
                          forceUppercase={false}
                        />
                        <p className="mb-4 text-xs text-gray-600">
                          Start typing to see address suggestions from Google Places
                        </p>
                        {selectedState === 'VIC' && (
                          <Select
                            label="Search Radius"
                            value={searchRadius.toString()}
                            onChange={(e) => setSearchRadius(Number(e.target.value))}
                            options={[
                              { value: '100', label: '100 meters' },
                              { value: '250', label: '250 meters' },
                              { value: '500', label: '500 meters' },
                              { value: '1000', label: '1 kilometer' },
                              { value: '2000', label: '2 kilometers' },
                            ]}
                          />
                        )}
                        <Button onClick={handleSearch} isLoading={isSearching}>
                          Search by Address
                        </Button>
                      </div>
                    ),
                  },
                ]}
              />
            ) : (
              <div className="rounded-lg border-2 border-dashed border-gray-200 py-12 text-center">
                <p className="text-base font-semibold text-gray-400">Coming Soon</p>
                <p className="mt-1 text-sm text-gray-400">
                  {selectedState} search is not yet available.
                </p>
              </div>
            )}
          </Card>

          {/* VIC Address Lookup Result */}
          {addressLookup && (
            <Card className="mt-6">
              <h3 className="mb-4 text-xl font-bold text-gray-800">Address Resolved</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-s font-bold text-gray-700">Address:</p>
                  <p className="text-s text-gray-600">{addressLookup.address}</p>
                </div>

                <div>
                  <p className="text-s font-bold text-gray-700">Coordinates:</p>
                  <p className="text-s text-gray-600">
                    {addressLookup.coordinates.lat.toFixed(6)},{' '}
                    {addressLookup.coordinates.lng.toFixed(6)}
                  </p>
                  <div className="mt-2">
                    {selectedState === 'VIC' && (
                      <Button
                        size="sm"
                        onClick={() => window.open('https://maps.land.vic.gov.au/lassi/', '_blank')}
                      >
                        View in LASSI
                      </Button>
                    )}
                    {selectedState === 'NSW' && (
                      <Button
                        size="sm"
                        onClick={() =>
                          window.open(
                            'https://portal.spatial.nsw.gov.au/explorer/index.html',
                            '_blank'
                          )
                        }
                      >
                        View in NSW Spatial
                      </Button>
                    )}
                  </div>
                </div>

                {addressLookup.lga && (
                  <div>
                    <p className="text-s font-bold text-gray-700">Local Government Area:</p>
                    <p className="text-s text-gray-600">{addressLookup.lga}</p>
                  </div>
                )}

                {addressLookup.planning && (
                  <div>
                    <p className="mb-2 text-s font-bold text-gray-700">Planning Information:</p>

                    {addressLookup.planning.zone && (
                      <div className="mb-3 rounded-lg bg-gold-light p-3">
                        <p className="text-xs font-bold uppercase text-gray-700">Zone</p>
                        <p className="text-s font-bold text-gold">
                          {addressLookup.planning.zone.code}
                        </p>
                        <p className="text-xs text-gray-600">
                          {addressLookup.planning.zone.description}
                        </p>
                      </div>
                    )}

                    {addressLookup.planning.overlays.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs font-bold uppercase text-gray-700">Overlays</p>
                        <div className="space-y-2">
                          {addressLookup.planning.overlays.map((overlay, idx) => (
                            <div key={idx} className="rounded-lg bg-gray-100 p-2">
                              <p className="text-s font-bold text-gray-800">{overlay.code}</p>
                              <p className="text-xs text-gray-600">{overlay.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* NSW Lot Result */}
          {nswLotResult && (
            <Card className="mt-6">
              <h3 className="mb-4 text-xl font-bold text-gray-800">NSW Lot Found</h3>
              <div className="rounded-lg border border-gray-200 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-bold text-gray-800">
                      Lot {nswLotResult.lotNumber} {nswLotResult.planLabel}
                    </h4>
                    {nswLotResult.area > 0 && (
                      <p className="text-xs text-gray-500">
                        Area: {nswLotResult.area.toLocaleString()} m²
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Subdivision plan will be detected automatically
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() =>
                      navigate(`/edit/${nswLotResult.planLabel}`, {
                        state: {
                          lotNumber: nswLotResult.lotNumber,
                          address: nswLotResult.address,
                          state: 'NSW',
                        },
                      })
                    }
                  >
                    Load Estate
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Loading indicator for parcel search */}
          {isSearching &&
            (selectedState === 'VIC' || selectedState === 'NSW') &&
            searchMode === 'address' && (
              <Card className="mt-6">
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-gold border-t-transparent"></div>
                    <p className="text-s font-bold text-gray-700">
                      {selectedState === 'NSW'
                        ? 'Finding NSW cadastral lot...'
                        : 'Searching for parcels...'}
                    </p>
                    {selectedState === 'VIC' && (
                      <p className="text-xs text-gray-600">
                        Looking for plan numbers within {searchRadius}m
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            )}

          {/* Parcel/Plan Number Results from Vicmap */}
          {parcelResults.length > 0 && (
            <Card className="mt-6">
              <h3 className="mb-4 text-xl font-bold text-gray-800">
                Plan Numbers Found ({parcelResults.length})
              </h3>
              <p className="mb-4 text-xs text-gray-600">
                Parcels within {searchRadius}m of the selected address (from Vicmap Parcel data):
              </p>
              <div className="space-y-3">
                {parcelResults.map((parcel, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border border-gray-200 p-4 transition-colors hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-800">
                          {parcel.planNumber}
                          {parcel.lotNumber && ` - Lot ${parcel.lotNumber}`}
                        </h4>
                        {parcel.spi && <p className="text-xs text-gray-600">SPI: {parcel.spi}</p>}
                        {(parcel.allotment || parcel.block || parcel.subdivision) && (
                          <p className="text-xs text-gray-500">
                            {[
                              parcel.allotment && `Allotment: ${parcel.allotment}`,
                              parcel.block && `Block: ${parcel.block}`,
                              parcel.subdivision && `Subdivision: ${parcel.subdivision}`,
                            ]
                              .filter(Boolean)
                              .join(' • ')}
                          </p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          Distance: {parcel.distance}m from address
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() =>
                          navigate(`/edit/${parcel.planNumber}`, {
                            state: {
                              address: addressLookup?.address,
                              state: 'VIC',
                            },
                          })
                        }
                      >
                        Load Estate
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* No parcels found */}
          {addressLookup &&
            parcelResults.length === 0 &&
            !isSearching &&
            !error &&
            selectedState === 'VIC' &&
            searchMode === 'address' && (
              <Card className="mt-6">
                <div className="rounded-lg bg-amber-50 p-4">
                  <p className="text-sm font-bold text-amber-800">No plan numbers found nearby</p>
                  <p className="mt-1 text-sm text-amber-700">
                    No Vicmap parcels were found within {searchRadius}m of this address. Try:
                  </p>
                  <ul className="mt-2 list-disc pl-5 text-sm text-amber-700">
                    <li>Increasing the search radius</li>
                    <li>Checking the address is correct</li>
                    <li>Switching to Plan Number search if you already know the PS number</li>
                  </ul>
                </div>
              </Card>
            )}

          {/* Error State */}
          {error && (
            <Card className="mt-6">
              <div className="rounded-lg bg-red-50 p-4">
                <p className="text-s font-bold text-red-800">Error</p>
                <p className="text-s text-red-600">{error}</p>
              </div>
            </Card>
          )}

          {/* Empty State */}
          {!isSearching && !addressLookup && !nswLotResult && !error && (
            <Card className="mt-6">
              <EmptyState
                title="No results yet"
                description={
                  searchMode === 'address' && selectedState !== 'VIC' && selectedState !== 'NSW'
                    ? `Address search is not available for ${selectedState}. Switch to Plan Number search.`
                    : searchMode === 'address'
                      ? 'Start typing an address to see suggestions, then click Search.'
                      : 'Enter a plan number above and click Search.'
                }
              />
            </Card>
          )}
        </div>

        {/* Right Sidebar */}
        <div>
          <Card>
            <h3 className="mb-4 text-xl font-bold text-gray-800">Import JSON</h3>
            <p className="mb-4 text-s text-gray-600">
              Open a previously exported estate JSON file to continue editing.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImportJson}
              className="hidden"
            />
            <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>
              Open JSON File
            </Button>
          </Card>

          <Card className="mt-6">
            <h3 className="mb-4 text-xl font-bold text-gray-800">Estate Preview</h3>
            {addressLookup ? (
              <div>
                <p className="mb-2 text-s text-gray-600">Address resolved successfully!</p>
                <div className="rounded-lg bg-gray-100 p-4">
                  <p className="text-xs text-gray-600">Map placeholder</p>
                  <p className="text-xs text-gray-500">
                    {addressLookup.coordinates.lat.toFixed(4)},{' '}
                    {addressLookup.coordinates.lng.toFixed(4)}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-s text-gray-600">
                Select an estate from the search results to see a preview.
              </p>
            )}
          </Card>

          {selectedState === 'VIC' && searchMode === 'address' && (
            <Card className="mt-6">
              <h3 className="mb-2 text-l font-bold text-gray-800">APIs Used</h3>
              <p className="mb-2 text-xs text-gray-600">This search uses live data from:</p>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>• Google Places (address autocomplete)</li>
                <li>• Vicmap Parcel (plan numbers & lots)</li>
                <li>• Vicmap Planning (zones & overlays)</li>
              </ul>
              <div className="mt-3 rounded-lg bg-green-50 p-2">
                <p className="text-xs text-green-800">
                  ✓ Fast, reliable search powered by Google Places
                </p>
              </div>
            </Card>
          )}
          {selectedState === 'NSW' && (
            <Card className="mt-6">
              <h3 className="mb-2 text-l font-bold text-gray-800">APIs Used</h3>
              <p className="mb-2 text-xs text-gray-600">NSW search uses live data from:</p>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>• Google Places (address autocomplete)</li>
                <li>• NSW SIX Cadastre (lot boundaries & plans)</li>
              </ul>
              <div className="mt-3 rounded-lg bg-green-50 p-2">
                <p className="text-xs text-green-800">
                  ✓ All processing done client-side — no backend job required
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
